export type RoleItem = { name?: string | null } | string;

export type UserData = {
  fullName?: string | null;
  full_name?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
  roles?: RoleItem[] | RoleItem | null;
  role?: RoleItem | null;
};

export interface UserProfile {
  fullName?: string | null;
  full_name?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
  [key: string]: unknown;
}

export type ProfileResponse = {
  user?: UserData | null;
  profile?: UserProfile | null;
  roles?: RoleItem[] | RoleItem | null;
  role?: RoleItem | null;
};

/**
 * Normalizes the API response data into a consistent ProfileResponse or UserData structure.
 * Handles Axios response wrapper if present.
 */
export const normalizeProfileData = (
  data: unknown,
): ProfileResponse | UserData | null => {
  if (!data) return null;
  return (
    (data as { data?: ProfileResponse | UserData }).data ??
    (data as ProfileResponse | UserData)
  );
};

/**
 * Extracts the main UserData object from the normalized profile data.
 */
export const extractUserData = (
  profileData: ProfileResponse | UserData,
): UserData => {
  return "user" in profileData && profileData.user
    ? profileData.user
    : (profileData as UserData);
};

/**
 * Extracts the user profile object (if distinct from UserData) from normalized profile data.
 */
export const extractUserProfile = (
  profileData: ProfileResponse | UserData,
): UserProfile => {
  return "profile" in profileData ? (profileData.profile ?? {}) : {};
};

/**
 * Extracts and normalizes roles from profile and user data into a string array.
 */
export const extractRoles = (
  profileData: ProfileResponse | UserData,
  userData: UserData,
): string[] => {
  const rawRoles =
    ("roles" in profileData ? profileData.roles : undefined) ||
    userData.roles ||
    (userData.role ? [userData.role] : []) ||
    ("role" in profileData && profileData.role ? [profileData.role] : []) ||
    [];

  return Array.isArray(rawRoles)
    ? rawRoles.map((r: unknown) => {
        const roleName =
          typeof r === "object" && r !== null && "name" in r
            ? (r as { name: string }).name
            : r;
        return String(roleName).trim();
      })
    : [];
};

/**
 * Checks if the profile is incomplete based on available data and roles.
 * Returns true if profile setup is needed.
 */
export const checkProfileIncompleteness = (
  userData: UserData,
  userProfile: UserProfile,
  roles: string[],
): boolean => {
  const fullName =
    userData.fullName ||
    userData.full_name ||
    userData.name ||
    userProfile.fullName ||
    userProfile.full_name ||
    userProfile.name;

  const phone = userData.phoneNumber || userProfile.phoneNumber;

  const hasFullName =
    typeof fullName === "string" && fullName.trim().length > 0;
  const hasPhone = typeof phone === "string" && phone.trim().length > 0;

  // Relax check for Organizers (they might register via email only)
  const isOrganizer = roles.some((r) => r.toLowerCase() === "organizer");

  if (isOrganizer) {
    return !hasFullName; // Organizers only need full name
  }

  // Return true if EITHER is missing (meaning incomplete)
  return !(hasFullName && hasPhone);
};
